import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Minus, X, Send, Loader2, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useFloatingChat } from '../contexts/FloatingChatContext';
import { marketBoardApi, friendsApi, matchesApi } from '../lib/api';

const POLL_MS = 5000;

const timeAgo = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return 'ora';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}g`;
};

// ---- Per-type adapters (return {load, send, markRead}) -------------------
function buildAdapter(chat) {
  switch (chat.type) {
    case 'market': {
      const { adId, otherUserId } = chat.params;
      return {
        load: async () => {
          const res = await marketBoardApi.getThread(adId, otherUserId);
          return (res.data?.messages || []).map((m) => ({
            id: m.message_id,
            from_user_id: m.from_user_id,
            from_gamertag: m.from_gamertag,
            from_picture: m.from_picture,
            content: m.content,
            created_at: m.created_at,
          }));
        },
        send: (content) => marketBoardApi.replyInThread(adId, otherUserId, content),
        markRead: null, // getThread already marks as read server-side
      };
    }
    case 'friend': {
      const { friendshipId } = chat.params;
      return {
        load: async () => {
          const res = await friendsApi.getChat(friendshipId);
          return (res.data?.messages || []).map((m) => ({
            id: m.message_id,
            from_user_id: m.from_user_id,
            from_gamertag: m.from_gamertag || '',
            from_picture: m.from_picture || null,
            content: m.content,
            created_at: m.created_at,
          }));
        },
        send: (content) => friendsApi.sendMessage(friendshipId, content),
        markRead: () => friendsApi.markRead(friendshipId),
      };
    }
    case 'match': {
      const { matchId } = chat.params;
      return {
        load: async () => {
          const res = await matchesApi.getMatchChat(matchId);
          return (res.data?.messages || []).map((m) => ({
            id: m.message_id,
            from_user_id: m.from_user_id,
            from_gamertag: m.from_gamertag || '',
            from_picture: m.from_picture || null,
            content: m.content,
            created_at: m.created_at,
            system: !!m.is_system,
          }));
        },
        send: (content) => matchesApi.postMatchChat(matchId, content),
        markRead: () => matchesApi.markMatchChatRead(matchId),
      };
    }
    default:
      return { load: async () => [], send: async () => {}, markRead: null };
  }
}

// ---- Single floating chat window ----------------------------------------
const ChatWindow = ({ chat }) => {
  const { user } = useAuth();
  const { closeChat, toggleMinimize } = useFloatingChat();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef(null);
  const adapterRef = useRef(buildAdapter(chat));
  const lastCountRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const refresh = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const msgs = await adapterRef.current.load();
      setMessages(msgs);
      if (msgs.length !== lastCountRef.current) {
        lastCountRef.current = msgs.length;
        setTimeout(scrollToBottom, 30);
      }
    } catch {
      /* network error, retry on next tick */
    } finally {
      if (!silent) setLoading(false);
    }
  }, [scrollToBottom]);

  // Initial load + polling while expanded
  useEffect(() => {
    refresh(false);
    if (chat.minimized) return undefined;
    const id = setInterval(() => refresh(true), POLL_MS);
    return () => clearInterval(id);
  }, [refresh, chat.minimized]);

  // Mark-read whenever expanded
  useEffect(() => {
    if (chat.minimized) return;
    const m = adapterRef.current.markRead;
    if (m) m().catch(() => {});
  }, [chat.minimized, messages.length]);

  const handleSend = async () => {
    const t = draft.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await adapterRef.current.send(t);
      setDraft('');
      await refresh(true);
    } catch (err) {
      // keep draft so user can retry
    } finally {
      setSending(false);
    }
  };

  const accent = chat.type === 'market' ? '#FF00E5' : chat.type === 'friend' ? '#00F0FF' : '#FFEA00';

  // ---- Minimized pill -----------------------------------------------------
  if (chat.minimized) {
    return (
      <button
        onClick={() => toggleMinimize(chat.key)}
        className="flex items-center gap-2 h-10 px-3 bg-[#0a0d15] border border-white/15 rounded-full hover:border-white/40 transition-colors shadow-lg"
        style={{ borderColor: `${accent}40` }}
        data-testid={`floating-chat-pill-${chat.key}`}
        title={chat.title}
      >
        {chat.avatar ? (
          <img src={chat.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
        ) : (
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-black"
            style={{ background: accent }}
          >
            {(chat.title || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <span className="text-xs text-white max-w-[120px] truncate">{chat.title}</span>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); closeChat(chat.key); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); closeChat(chat.key); } }}
          className="text-slate-500 hover:text-white p-0.5 -mr-1 cursor-pointer"
          data-testid={`floating-chat-pill-close-${chat.key}`}
          aria-label="Chiudi chat"
        >
          <X size={12} />
        </span>
      </button>
    );
  }

  // ---- Expanded window ----------------------------------------------------
  return (
    <div
      className="flex flex-col w-[300px] sm:w-[320px] h-[420px] bg-[#0a0d15] border border-white/15 rounded-t-lg shadow-2xl overflow-hidden"
      style={{ borderColor: `${accent}55` }}
      data-testid={`floating-chat-window-${chat.key}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 h-10 border-b border-white/10 shrink-0" style={{ background: `${accent}10` }}>
        {chat.avatar ? (
          <img src={chat.avatar} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
        ) : (
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-black shrink-0"
            style={{ background: accent }}
          >
            {(chat.title || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs text-white font-semibold truncate leading-tight">{chat.title}</p>
          {chat.subtitle && (
            <p className="text-[10px] text-slate-400 truncate leading-tight">{chat.subtitle}</p>
          )}
        </div>
        <button
          onClick={() => toggleMinimize(chat.key)}
          className="text-slate-400 hover:text-white p-1 rounded hover:bg-white/5"
          data-testid={`floating-chat-minimize-${chat.key}`}
          aria-label="Minimizza"
          title="Minimizza"
        >
          <Minus size={14} />
        </button>
        <button
          onClick={() => closeChat(chat.key)}
          className="text-slate-400 hover:text-[#FF1744] p-1 rounded hover:bg-white/5"
          data-testid={`floating-chat-close-${chat.key}`}
          aria-label="Chiudi"
          title="Chiudi"
        >
          <X size={14} />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-8 text-slate-500 text-xs">
            <Loader2 size={12} className="animate-spin" />
            Caricamento…
          </div>
        )}
        {!loading && messages.length === 0 && (
          <p className="text-center text-slate-600 text-xs py-8">
            Nessun messaggio ancora.
          </p>
        )}
        {!loading && messages.map((m) => {
          if (m.system) {
            return (
              <div key={m.id} className="text-center text-[10px] text-slate-500 italic py-1">
                {m.content}
              </div>
            );
          }
          const mine = m.from_user_id === user?.user_id;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] px-2.5 py-1 rounded-md text-[12px] ${
                  mine
                    ? 'bg-white/10 border border-white/20 text-white'
                    : 'bg-[#050508] border border-white/10 text-slate-200'
                }`}
                style={mine ? { borderColor: `${accent}55`, background: `${accent}15` } : {}}
              >
                {!mine && m.from_gamertag && (
                  <p className="text-[9px] text-slate-500 mb-0.5">{m.from_gamertag}</p>
                )}
                <p className="whitespace-pre-wrap break-words leading-snug">{m.content}</p>
                <p className="text-[8px] text-slate-500 mt-0.5 text-right">{timeAgo(m.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="px-2 py-2 border-t border-white/10 shrink-0 flex gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          maxLength={2000}
          placeholder="Scrivi un messaggio…"
          className="flex-1 bg-[#050508] border border-white/10 text-white text-xs rounded px-2.5 py-1.5 focus:outline-none focus:border-white/30"
          disabled={sending}
          data-testid={`floating-chat-input-${chat.key}`}
        />
        <button
          onClick={handleSend}
          disabled={sending || !draft.trim()}
          className="shrink-0 px-2 rounded text-black font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: accent }}
          data-testid={`floating-chat-send-${chat.key}`}
          aria-label="Invia"
        >
          {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
        </button>
      </div>
    </div>
  );
};

// ---- Root manager rendered once at <App/> level --------------------------
const FloatingChatRoot = () => {
  const { user } = useAuth();
  const { chats } = useFloatingChat();
  if (!user || chats.length === 0) return null;

  return (
    <div
      className="fixed bottom-0 right-3 sm:right-4 z-[9999] flex flex-row-reverse items-end gap-2 pointer-events-none"
      data-testid="floating-chat-root"
    >
      {chats.map((c) => (
        <div key={c.key} className="pointer-events-auto">
          <ChatWindow chat={c} />
        </div>
      ))}
    </div>
  );
};

// Tiny indicator-icon export so other components can re-use it if desired
export const FloatingChatIcon = (props) => <MessageSquare {...props} />;

export default FloatingChatRoot;
